import React, {
  forwardRef,
  ReactNode,
  Ref,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  ViewStyle,
} from 'react-native';

import generateID from '../../utils/generate-id';

type Props = {
  accessibilityLabel?: string;
  animationDuration?: number;
  children?: ReactNode;
  onOpen?: () => void;
  onPress?: () => void;
  onClose?: () => void;
  open?: boolean;
  style?: StyleProp<ViewStyle>;
  id?: string;
};

const DEFAULT_ANIM_DURATION = 250;

const Backdrop = forwardRef(
  (props: Props, ref: Ref<TouchableWithoutFeedback>) => {
    const [isMounted, setIsMounted] = useState(false);
    const opacity = useRef(new Animated.Value(0)).current;

    useEffect(() => {
      if (!isMounted) {
        setIsMounted(true);
      } else {
        Animated.timing(opacity, {
          toValue: props.open ? 1 : 0,
          duration: props.animationDuration || DEFAULT_ANIM_DURATION,
          useNativeDriver: true,
        }).start(() => {
          if (props.open) {
            props.onOpen?.();
          } else {
            props.onClose?.();
          }
        });
      }
    }, [props.open, isMounted]);

    return (
      <TouchableWithoutFeedback
        ref={ref}
        onPress={props.onPress}
        accessibilityLabel={props.accessibilityLabel}
        /** Generate HTML and Test IDs  */
        {...generateID(props.id)}
        /** Web-specific props */
        {...Platform.select({
          web: {
            accessibilityModal: true, // TODO: REQUIRES REACT 17 TO WORK, SO PLEASE UPGRADE
          },
        })}
      >
        <Animated.View style={[style.root, props.style, { opacity }]}>
          {props.children}
        </Animated.View>
      </TouchableWithoutFeedback>
    );
  }
);

const style = StyleSheet.create({
  root: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(40, 60, 70, 0.5)',
    overflow: 'hidden',
  },
});

export default Backdrop;
